/**
 * RoomList — `/rooms` (and `/mediation` alias)
 *
 * Three views, mirroring the rooms-page design:
 *  - empty:   gradient hero panel + 3-step "how it works" strip + two CTAs
 *  - compose: inline composer (person picker → reason → channel → send)
 *  - list:    flat status-pill-driven list of rooms, ended collapsed at bottom
 *
 * The composer's send action navigates to /f8chat with the composed context
 * as query params. Direct createRoomInvite from this page is a
 * follow-up — it needs a Contact → tetherUser uid lookup we don't have here.
 */

import { useEffect, useState, useMemo, useRef, CSSProperties } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronUp, AlertCircle, Loader, Check, UserPlus, Users, Wind, Zap } from 'react-feather';
import { useAuth } from '../hooks/useAuth';
import { getMyProfile } from '../services/accountService';
import { useAuthGate } from '../hooks/useAuthGate';
import useMediaQuery from '../hooks/useMediaQuery';
import SEO from '../components/SEO';
import { getMyRooms, createRoomInvite, resolveTetherHandle, lookupUserByEmail, generateRoomOpener, toInviterRelationship, getBreakTheIceMatches } from '../services/roomService';
import type { MatchCandidate } from '../services/roomService';
import { FLAG_READ_TIMEOUT_MS, isMeetAMatchEnabled, withTimeout } from '../services/featureFlags';
import { getContacts, getContactChannels } from '../services/contactService';
import { capture } from '../services/posthog';
import type { RoomComposeTarget, RoomListItem } from '../types/room';
import type { Contact } from '../types/contacts';
import { normalizeInterests } from '../utils/interests';
// Brand icon (same asset Navigation uses for the collapsed rail) — for the
// on-pplkpr detection card in the composer.
import iconImg from '../../images/icon_transparent.png';
import '../styles/RoomList.css';

// ─── helpers ─────────────────────────────────────────

function timeAgo(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString();
}

type PillType = 'invited' | 'waiting' | 'active' | 'prep' | 'waitingThem' | 'opening' | 'paused';
type Channel = 'in_app' | 'email' | 'sms' | 'link';

const PILL_STYLES: Record<PillType, { label: string; bg: string; border: string; color: string; dot: string; live: boolean }> = {
  invited:     { label: 'invite sent',     bg: 'rgba(251,146,60,0.14)',  border: 'rgba(251,146,60,0.4)',   color: '#fcd9b6', dot: '#fb923c', live: false },
  waiting:     { label: 'waiting on you',  bg: 'rgba(217,70,239,0.16)',  border: 'rgba(217,70,239,0.45)',  color: '#f5d0fe', dot: '#d946ef', live: false },
  active:      { label: 'room open',       bg: 'rgba(34,197,94,0.14)',   border: 'rgba(34,197,94,0.45)',   color: '#bbf7d0', dot: '#22c55e', live: true  },
  prep:        { label: 'in prep',         bg: 'rgba(99,102,241,0.18)',  border: 'rgba(129,140,248,0.45)', color: '#c7d2fe', dot: '#818cf8', live: false },
  waitingThem: { label: 'waiting on them', bg: 'rgba(168,85,247,0.16)',  border: 'rgba(168,85,247,0.45)',  color: '#e9d5ff', dot: '#a855f7', live: false },
  opening:     { label: 'opening room',    bg: 'rgba(34,197,94,0.14)',   border: 'rgba(34,197,94,0.4)',    color: '#bbf7d0', dot: '#22c55e', live: true  },
  paused:      { label: 'paused',          bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.12)', color: 'rgba(232,220,250,0.6)', dot: 'rgba(255,255,255,0.4)', live: false },
};

const STAGE_LABELS = ['invite', 'prep', 'rules', 'opening', 'room'];

// A `prep` room where the caller has already saved their rules has, for that
// caller, advanced into the waiting room — they're done, waiting on the other
// side. The shared `state` only flips out of `prep` once BOTH sides save, so we
// fold `myRulesSaved` in here to route/label that one side correctly.
function inWaitingRoom(item: RoomListItem): boolean {
  return item.state === 'prep' && item.myRulesSaved;
}

function statusFor(item: RoomListItem): PillType {
  if (item.state === 'invited') return item.isInviter ? 'invited' : 'waiting';
  if (inWaitingRoom(item)) return 'waitingThem';
  if (item.state === 'prep') return 'prep';
  if (item.state === 'rules_set') return 'opening';
  if (item.state === 'joint') return 'waiting';
  if (item.state === 'ongoing') return 'active';
  if (item.state === 'paused') return 'paused';
  return 'prep';
}

function stageFor(item: RoomListItem): number {
  if (inWaitingRoom(item)) return 3;
  switch (item.state) {
    case 'invited':   return 1;
    case 'prep':      return 2;
    case 'rules_set': return 4;
    case 'joint':     return 4;
    case 'ongoing':   return 5;
    case 'paused':    return 5;
    case 'ended':     return 5;
  }
}

function blurbFor(item: RoomListItem): string {
  const them = item.otherParticipant.firstName || 'they';
  switch (item.state) {
    case 'invited':
      return item.isInviter
        ? `invitation sent — ${them} can accept, decline, or ask to know more.`
        : `${them} wants to open a room with you.`;
    case 'prep':       return item.myRulesSaved
      ? `you're set — waiting for ${them} to confirm their rules.`
      : 'each side is talking to f8 alone first.';
    case 'rules_set':  return 'both confirmed — f8 is opening the room…';
    case 'joint':      return `f8 will open the room when you're both ready.`;
    case 'ongoing':    return 'the room is open.';
    case 'paused':     return 'paused — either side can re-open.';
    case 'ended':
      return item.endedReason === 'completed'   ? 'closed with a summary.'
           : item.endedReason === 'declined'    ? 'declined.'
           : item.endedReason === 'withdrawn'   ? 'withdrawn.'
           : item.endedReason === 'prep_enough' ? 'prep was enough.'
           : 'ended.';
  }
}

function routeFor(item: RoomListItem): string {
  const id = item.id;
  if (inWaitingRoom(item)) return `/rooms/${id}/waiting`;
  switch (item.state) {
    case 'invited':    return item.isInviter ? `/rooms/${id}` : `/rooms/${id}/invite`;
    case 'prep':       return `/rooms/${id}/prep`;
    case 'rules_set':  return `/rooms/${id}/opener`;
    case 'joint':      return `/rooms/${id}/opener`;
    case 'ongoing':    return `/rooms/${id}/room`;
    case 'paused':     return `/rooms/${id}/room`;
    case 'ended':      return `/rooms/${id}/summary`;
  }
}

function hueFromName(name: string): number {
  // Handle-named targets ('@sunsetmediator') must hash to the same hue as the
  // bare handle, so the picked-person card's tint and its avatar agree.
  const clean = name.replace(/^@/, '');
  let h = 0;
  for (let i = 0; i < clean.length; i++) h = (h * 31 + clean.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
}

// ─── atoms ───────────────────────────────────────────

function Dot({ size = 14, glow = true, pulse = false }: { size?: number; glow?: boolean; pulse?: boolean }) {
  return (
    <span style={{
      display: 'inline-block', width: size, height: size, borderRadius: '50%',
      background: 'radial-gradient(circle at 30% 30%, #f0abfc, #d946ef 50%, #7c3aed)',
      boxShadow: glow ? `0 0 ${size * 0.7}px rgba(217,70,239,0.7)` : 'none',
      animation: pulse ? 'rooms-pulse 2.4s ease-in-out infinite' : 'none', flexShrink: 0,
    }} />
  );
}

function StatusPill({ status }: { status: PillType }) {
  const s = PILL_STYLES[status];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 11px 4px 9px', borderRadius: 999,
      background: s.bg, border: `1px solid ${s.border}`,
      fontSize: 11.5, fontWeight: 700, color: s.color, whiteSpace: 'nowrap',
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%', background: s.dot,
        boxShadow: s.live ? `0 0 7px ${s.dot}` : 'none',
        animation: s.live ? 'rooms-pulse 1.8s ease-in-out infinite' : 'none',
      }} />
      {s.label}
    </span>
  );
}

function StageDots({ stage }: { stage: number }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      {STAGE_LABELS.map((label, i) => {
        const n = i + 1;
        const cur = n === stage;
        const done = n < stage;
        return (
          <span key={label} style={{
            width: cur ? 16 : 6, height: 6, borderRadius: 999,
            background: cur
              ? 'linear-gradient(90deg,#f0abfc,#a855f7)'
              : done
                ? 'rgba(217,70,239,0.45)'
                : 'rgba(255,255,255,0.14)',
          }} />
        );
      })}
      <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 600, color: 'rgba(232,220,250,0.5)' }}>{STAGE_LABELS[stage - 1]}</span>
    </div>
  );
}

function HueAvatar({ name, size = 44 }: { name: string; size?: number }) {
  // Handle-named targets ('@sunsetmediator') would otherwise initial as '@'.
  const clean = name.replace(/^@/, '');
  const hue = hueFromName(name);
  const initials = clean.split(' ').map((w) => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase();
  return (
    <span style={{
      width: size, height: size, borderRadius: '50%',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: `radial-gradient(circle at 30% 30%, hsla(${hue},80%,70%,0.55), hsla(${hue},75%,38%,0.85))`,
      border: `1px solid hsla(${hue},75%,60%,0.4)`,
      fontWeight: 700, fontSize: size * 0.36, color: '#fff', letterSpacing: '0.04em', flexShrink: 0,
    }}>{initials || '?'}</span>
  );
}

function RoomsGlyph({ size = 18, color = '#fff' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="8" cy="8.5" r="3.2" stroke={color} strokeWidth="1.8" />
      <circle cx="16" cy="8.5" r="3.2" stroke={color} strokeWidth="1.8" />
      <path d="M3 19c0-2.5 2.2-4.2 5-4.2M21 19c0-2.5-2.2-4.2-5-4.2" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function ChatGlyph({ size = 16, color = '#f0abfc' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M21 12a8 8 0 0 1-12 7l-5 1 1.5-4.5A8 8 0 1 1 21 12Z" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function SparkleGlyph({ size = 12, color = 'rgba(232,220,250,0.7)' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 3l2.2 6.3L20.5 11l-6.3 1.7L12 19l-2.2-6.3L3.5 11l6.3-1.7L12 3z" fill={color} />
    </svg>
  );
}

function SearchGlyph({ color = 'rgba(232,220,250,0.5)' }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="11" cy="11" r="7" stroke={color} strokeWidth="1.8" />
      <path d="M20 20l-3.5-3.5" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function EmailGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="#fff" strokeWidth="1.6" />
      <path d="M3 7l9 7 9-7" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

function SmsGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M21 12a8 8 0 0 1-12 7l-5 1 1.5-4.5A8 8 0 1 1 21 12Z" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

function LinkGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M10 14a3 3 0 0 1 0-4l4-4a3 3 0 1 1 4 4l-2 2" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M14 10a3 3 0 0 1 0 4l-4 4a3 3 0 1 1-4-4l2-2" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function ChevronLeftGlyph({ size = 14, color = 'rgba(232,220,250,0.6)' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M15 6l-6 6 6 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRightGlyph({ size = 14, color = 'rgba(232,220,250,0.4)' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M9 6l6 6-6 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlusGlyph({ size = 15, color = '#f0abfc' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 5v14M5 12h14" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

// ─── shared button styles ────────────────────────────

const primaryBtn: CSSProperties = {
  appearance: 'none', border: 0, cursor: 'pointer', whiteSpace: 'nowrap',
  display: 'inline-flex', alignItems: 'center', gap: 9,
  padding: '13px 22px', borderRadius: 999,
  background: 'linear-gradient(135deg,#d946ef,#a855f7)', color: '#fff',
  fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
  boxShadow: '0 8px 24px rgba(217,70,239,0.32)',
};

const ghostBtn: CSSProperties = {
  appearance: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
  display: 'inline-flex', alignItems: 'center', gap: 9,
  padding: '13px 20px', borderRadius: 999,
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.13)',
  color: 'rgba(232,220,250,0.9)', fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
};

/** PPL-506 — the quiet link-styled actions on the picked / candidate cards. */
const pickedActionBtn: CSSProperties = {
  appearance: 'none', border: 0, cursor: 'pointer', background: 'transparent', padding: 0,
  color: '#f0abfc', fontWeight: 600, fontSize: 12.5, fontFamily: 'inherit',
};

/**
 * PPL-506 / PPL-389 — the public profile of a break-the-ice candidate, the
 * same `/user/:userId` page the desk's meet card opens. `from: 'rooms_match'`
 * keeps the match attribution (source 'match') for a target that really came
 * from the candidate list, without the profile's back button promising home;
 * a target opened from its own profile carries nothing, so a room started
 * there is not re-stamped as a match.
 */
function openProfile(
  navigate: ReturnType<typeof useNavigate>,
  candidateUid: string,
  source?: ComposeTarget['source'],
) {
  navigate(`/user/${candidateUid}`, {
    state: source === 'match' ? { from: 'rooms_match' } : undefined,
  });
}

/**
 * PPL-506 — a profile is a full page navigation on web, so the composer
 * unmounts and browser back lands on a freshly loaded /rooms with no router
 * state. Park the picked target and the typed reason for the round trip so
 * neither has to be redone; spent on the next load, whichever way we return.
 */
const RESUME_KEY = 'rooms:composeResume';
const RESUME_TTL_MS = 30 * 60 * 1000;
type ComposeResume = { target: ComposeTarget; draft: string; ts: number };

function stashComposeResume(target: ComposeTarget, draft: string) {
  try {
    sessionStorage.setItem(RESUME_KEY, JSON.stringify({ target, draft, ts: Date.now() }));
  } catch {
    // storage blocked (private mode) — the trip just costs the draft
  }
}

function takeComposeResume(): ComposeResume | null {
  try {
    const raw = sessionStorage.getItem(RESUME_KEY);
    sessionStorage.removeItem(RESUME_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ComposeResume;
    if (!parsed?.target?.key || Date.now() - (parsed.ts || 0) > RESUME_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearComposeResume() {
  try {
    sessionStorage.removeItem(RESUME_KEY);
  } catch {
    // nothing to clear
  }
}

const pickerInputStyle: CSSProperties = {
  width: '100%', padding: '12px 14px', borderRadius: 12,
  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
  color: '#fff', fontSize: 14, fontFamily: 'inherit', outline: 'none',
};

// ─── empty view ──────────────────────────────────────

function EmptyView({ onOpen, onTalk }: { onOpen: () => void; onTalk: () => void }) {
  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px 0 60px' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, letterSpacing: '-0.03em' }}>your rooms</h1>
        <p style={{ margin: '6px 0 0', fontSize: 14, color: 'rgba(232,220,250,0.55)' }}>conversations with f8 as a third presence</p>
      </div>

      <div style={{
        padding: 32, borderRadius: 24, position: 'relative', overflow: 'hidden',
        background: 'linear-gradient(180deg, rgba(217,70,239,0.10), rgba(124,58,237,0.04))',
        border: '1px solid rgba(217,70,239,0.22)',
      }}>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
            <span style={{
              width: 40, height: 40, borderRadius: '50%',
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(217,70,239,0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Dot size={17} pulse />
            </span>
            <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#f0abfc' }}>a room · with f8</span>
          </div>
          <div style={{ fontSize: 25, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.25, maxWidth: 540 }}>
            a calm, two-person space for the conversations that are hard to start
          </div>
          <p style={{ fontSize: 14.5, color: 'rgba(232,220,250,0.7)', lineHeight: 1.6, marginTop: 12, maxWidth: 560 }}>
            mend something with a sibling or partner, or have a careful first conversation with someone new. you each talk to f8 privately first — then meet, on a shared map instead of a fight. nothing crosses over unless you say so.
          </p>

          <div style={{ display: 'flex', gap: 12, marginTop: 24, flexWrap: 'wrap' }}>
            <button onClick={onOpen} style={primaryBtn}>
              <RoomsGlyph size={18} color="#fff" /> open a room
            </button>
            <button onClick={onTalk} style={ghostBtn}>
              <ChatGlyph size={16} color="#f0abfc" /> talk to f8 first
            </button>
          </div>
          <div style={{ fontSize: 12.5, color: 'rgba(232,220,250,0.45)', marginTop: 14 }}>
            you don&apos;t need to chat first — pick someone and f8 will help you reach out.
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginTop: 18 }}>
        {[
          ['1', 'you prep first', 'tell f8 your side privately'],
          ['2', 'they prep too', 'their own private space'],
          ['3', 'you meet', 'f8 opens from what you share'],
        ].map(([n, h, b]) => (
          <div key={n} style={{
            padding: '16px 16px', borderRadius: 16,
            background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{
                width: 22, height: 22, borderRadius: '50%',
                background: 'linear-gradient(135deg,#d946ef,#a855f7)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11.5, fontWeight: 800, flexShrink: 0,
              }}>{n}</span>
              <span style={{ fontSize: 13.5, fontWeight: 700 }}>{h}</span>
            </div>
            <div style={{ fontSize: 12, color: 'rgba(232,220,250,0.55)', lineHeight: 1.45 }}>{b}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── compose view ────────────────────────────────────

/**
 * Display stand-in when a prefilled target carries no name at all (a profile
 * with no handle). It reads naturally in the composer copy but is NOT a name,
 * so it must never be persisted as `inviteeName` on the invitation.
 *
 * Whether a target is nameless is carried by the `isNameless` FLAG, never by
 * comparing against this string: `name` also holds free text the user typed in
 * the new-person picker, so a user who genuinely types "them" as a nickname
 * would otherwise have their own input silently dropped — the same class of
 * bug (a wrong `inviteeName`) this stand-in exists to prevent.
 */
const NAMELESS_TARGET = 'them';

interface ComposeTarget {
  /** Local contact id when picked from my ppl, or 'new:<name>' for ad-hoc invite. */
  key: string;
  /**
   * What the composer renders for this person — and what every "…{firstName}…"
   * line reads. For anonymous-half targets (PPL-210 profile invites) this is
   * the `@handle`, never a real name.
   */
  name: string;
  /**
   * True only when `name` is the NAMELESS_TARGET stand-in rather than a real
   * name, so `inviteeName` is omitted on send. Set exclusively where a
   * handle-less profile prefill is constructed — never inferred from `name`.
   */
  isNameless?: boolean;
  /** Free-text relationship; '' when unknown, so no inviterRelationship is sent. */
  rel: string;
  isNewPerson: boolean;
  email?: string;
  phone?: string;
  /** Set when the contact is linked to a pplkpr user — enables direct send. */
  tetherHandle?: string;
  /** Profile image for the picked-person card, when the source had one. */
  avatar?: string;
  /**
   * PPL-171 — set when the target arrived already resolved (meet-a-match
   * candidates and PPL-210 profile invites carry their uid), so the composer
   * skips detection entirely.
   */
  resolvedUid?: string;
  /**
   * Analytics attribution for the send: 'match' = meet-a-match candidate,
   * 'profile' = the "invite to room" action on /user/:userId (PPL-210).
   */
  source?: 'match' | 'profile';
}

/** One of my ppl as a compose target — the picker's row and the home desk's preselect (PPL-417) build the same one. */
function contactTarget(c: Contact): ComposeTarget {
  const full = `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.nickname || 'unnamed';
  const rel = (c.relationship as string) || 'no relationship set';
  return { key: c.id, name: full, rel, isNewPerson: false, email: c.email, phone: c.phone, tetherHandle: c.tetherHandle };
}

function SectionLabel({ n, label }: { n: string; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
      <span style={{
        width: 20, height: 20, borderRadius: '50%',
        background: 'rgba(217,70,239,0.18)', border: '1px solid rgba(217,70,239,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10.5, fontWeight: 800, color: '#f0abfc',
      }}>{n}</span>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(232,220,250,0.55)', whiteSpace: 'nowrap' }}>{label}</span>
    </div>
  );
}

// ─── flow stage (design: pplkpr-clear-break-flow) ────
//
// The clear-the-air / break-the-ice flow pages pair the interactive card with
// a "how a room works" explainer — the same context the invitee reads in the
// email, now shown to the creator. Desktop: two-column grid, explainer sticky
// on the left, so the card no longer floats as a lonely centered column.
// Mobile: the explainer stacks compact above the card (footer hidden).

type FlowVariant = 'clear' | 'ice';

const FLOW_EYEBROW: Record<FlowVariant, string> = {
  clear: 'f8 · opening a room',
  ice: 'f8 · breaking the ice',
};

const ROOM_STEPS: Array<[string, string, string]> = [
  ['1', 'you prep first', 'tell f8 your side, privately.'],
  ['2', 'they prep too', 'their own private space — nothing crosses over.'],
  ['3', 'you meet', 'f8 opens the conversation from what you both choose to share.'],
];

function Explainer({ variant, compact }: { variant: FlowVariant; compact: boolean }) {
  const line = variant === 'clear'
    ? 'a room is a calm space for the conversation that’s hard to start — with someone you already know, and f8 in the middle.'
    : 'a room is a gentle way to start with someone new — a careful first conversation, with f8 making the introduction.';
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 16 }}>
        <span style={{
          width: 34, height: 34, borderRadius: '50%',
          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(217,70,239,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Dot size={15} pulse />
        </span>
        <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#f0abfc' }}>
          {FLOW_EYEBROW[variant]}
        </span>
      </div>

      <p style={{
        margin: compact ? '0 0 20px' : '0 0 26px', fontSize: compact ? 15.5 : 17, fontWeight: 600,
        color: '#fff', letterSpacing: '-0.015em', lineHeight: 1.5,
      }}>{line}</p>

      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(232,220,250,0.4)', marginBottom: 16 }}>
        how a room works
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 14 : 20 }}>
        {ROOM_STEPS.map(([n, h, b]) => (
          <div key={n} style={{ display: 'flex', gap: 13 }}>
            <span style={{
              width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg,#d946ef,#a855f7)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 800, color: '#fff',
            }}>{n}</span>
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>{h}</div>
              <div style={{ fontSize: 13, color: 'rgba(232,220,250,0.6)', lineHeight: 1.5, marginTop: 3 }}>{b}</div>
            </div>
          </div>
        ))}
      </div>

      {!compact && (
        <div style={{
          marginTop: 26, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.07)',
          fontSize: 12.5, color: 'rgba(232,220,250,0.55)', lineHeight: 1.6,
        }}>
          they can <b style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>accept</b>,{' '}
          <b style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>decline</b>, or{' '}
          <b style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>ask to know more</b> first.
          nothing from your prep with f8 goes with the invitation.
        </div>
      )}
    </div>
  );
}

function Stage({ variant, breadcrumbLabel, onBack, children }: {
  variant: FlowVariant;
  breadcrumbLabel: string;
  onBack: () => void;
  children: React.ReactNode;
}) {
  // ~820px of content width next to the 248px rail ↔ ~1080px viewport.
  const twoCol = useMediaQuery('(min-width: 1080px)');
  return (
    <div style={{ maxWidth: twoCol ? 1060 : 600, margin: '0 auto', padding: '28px 0 60px' }}>
      <button
        onClick={onBack}
        style={{
          appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer',
          color: 'rgba(232,220,250,0.6)', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
          display: 'inline-flex', alignItems: 'center', gap: 6,
          marginBottom: 18, padding: 0, whiteSpace: 'nowrap',
        }}
      >
        <ChevronLeftGlyph size={14} /> {breadcrumbLabel}
      </button>
      <div style={{
        display: 'grid',
        gridTemplateColumns: twoCol ? 'minmax(260px, 320px) minmax(0, 1fr)' : '1fr',
        gap: twoCol ? '22px 48px' : 20,
        alignItems: 'start',
      }}>
        <div style={twoCol ? { position: 'sticky', top: 32 } : undefined}>
          <Explainer variant={variant} compact={!twoCol} />
        </div>
        <div style={{ minWidth: 0 }}>{children}</div>
      </div>
    </div>
  );
}

function PersonPicker({
  contacts, query, setQuery, newMode, setNewMode, newName, setNewName, newContact, setNewContact, onPick,
}: {
  contacts: Contact[];
  query: string;
  setQuery: (s: string) => void;
  newMode: boolean;
  setNewMode: (b: boolean) => void;
  newName: string;
  setNewName: (s: string) => void;
  newContact: string;
  setNewContact: (s: string) => void;
  onPick: (p: ComposeTarget) => void;
}) {
  const q = query.toLowerCase();
  const filtered = contacts.filter((c) => {
    const full = `${c.firstName || ''} ${c.lastName || ''} ${c.nickname || ''}`.toLowerCase();
    return full.includes(q);
  });

  return (
    <div style={{ marginBottom: 22 }}>
      {!newMode ? (
        <>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 9,
            padding: '10px 14px', borderRadius: 12,
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)',
            marginBottom: 10,
          }}>
            <SearchGlyph />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="search your people…"
              style={{ flex: 1, background: 'transparent', border: 0, outline: 'none', color: '#fff', fontSize: 14, fontFamily: 'inherit' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 232, overflowY: 'auto' }}>
            {filtered.map((c) => {
              const full = `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.nickname || 'unnamed';
              const rel = (c.relationship as string) || 'no relationship set';
              return (
                <button
                  key={c.id}
                  onClick={() => onPick(contactTarget(c))}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  style={{
                    appearance: 'none', border: 0, cursor: 'pointer',
                    background: 'transparent', color: '#fff',
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '9px 10px', borderRadius: 12,
                    fontFamily: 'inherit', textAlign: 'left',
                  }}
                >
                  <HueAvatar name={full} size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{full}</div>
                    <div style={{ fontSize: 11.5, color: 'rgba(232,220,250,0.5)' }}>{rel}</div>
                  </div>
                  <ChevronRightGlyph />
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div style={{ padding: '12px 10px', fontSize: 13, color: 'rgba(232,220,250,0.5)' }}>
                no one by that name in your people.
              </div>
            )}
          </div>
          <button
            onClick={() => setNewMode(true)}
            style={{
              appearance: 'none', cursor: 'pointer', width: '100%',
              marginTop: 8, padding: '11px 14px', borderRadius: 12,
              background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.16)',
              color: 'rgba(232,220,250,0.8)', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 10,
            }}
          >
            <span style={{
              width: 28, height: 28, borderRadius: 9,
              background: 'rgba(217,70,239,0.16)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <PlusGlyph />
            </span>
            invite someone new — they don&apos;t need an account
          </button>
        </>
      ) : (
        <div style={{ padding: '4px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <button
              onClick={() => setNewMode(false)}
              style={{
                appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer',
                color: 'rgba(232,220,250,0.6)', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: 5, padding: 0,
              }}
            >
              <ChevronLeftGlyph size={13} /> back to your people
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="their name"
              style={pickerInputStyle}
            />
            <input
              value={newContact}
              onChange={(e) => setNewContact(e.target.value)}
              placeholder="email — how to reach them"
              style={pickerInputStyle}
            />
          </div>
          <button
            disabled={!newName.trim()}
            onClick={() => onPick({
              key: `new:${newName.trim()}`,
              name: newName.trim() || 'new person',
              rel: 'new connection',
              isNewPerson: true,
              email: newContact.includes('@') ? newContact : undefined,
              phone: !newContact.includes('@') ? newContact : undefined,
            })}
            style={{
              appearance: 'none', border: 0, cursor: newName.trim() ? 'pointer' : 'not-allowed',
              marginTop: 12, padding: '11px 18px', borderRadius: 999,
              background: newName.trim() ? 'linear-gradient(135deg,#d946ef,#a855f7)' : 'rgba(255,255,255,0.08)',
              color: '#fff', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700,
              opacity: newName.trim() ? 1 : 0.6,
            }}
          >
            continue
          </button>
          <p style={{ fontSize: 12, color: 'rgba(232,220,250,0.5)', lineHeight: 1.5, marginTop: 12 }}>
            rooms work for people you&apos;ve never spoken to here — a careful first conversation counts too.
          </p>
        </div>
      )}
    </div>
  );
}

function ChannelRow({
  on, onClick, label, sub, tag, icon,
}: {
  on: boolean;
  onClick: () => void;
  label: string;
  sub: string;
  tag?: string;
  icon: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        appearance: 'none', cursor: 'pointer', width: '100%', color: '#fff',
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 14px', borderRadius: 12,
        background: on ? 'rgba(217,70,239,0.10)' : 'rgba(255,255,255,0.03)',
        border: on ? '1px solid rgba(217,70,239,0.4)' : '1px solid rgba(255,255,255,0.08)',
        fontFamily: 'inherit', textAlign: 'left',
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 9,
        background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' }}>{label}</span>
          {tag && (
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
              color: '#f0abfc', background: 'rgba(217,70,239,0.15)',
              padding: '2px 6px', borderRadius: 999, border: '1px solid rgba(217,70,239,0.3)',
            }}>{tag}</span>
          )}
        </div>
        <div style={{ fontSize: 11.5, color: 'rgba(232,220,250,0.55)', marginTop: 2 }}>{sub}</div>
      </div>
      <div style={{
        width: 16, height: 16, borderRadius: '50%',
        border: on ? '4px solid #d946ef' : '1.5px solid rgba(255,255,255,0.25)',
        background: on ? '#fff' : 'transparent', flexShrink: 0,
      }} />
    </button>
  );
}

// PPL-60 — composer detection state. We branch the UI on whether the picked
// person is on pplkpr (by tetherHandle or email lookup) or not.
type DetectionState =
  | { status: 'idle' }
  | { status: 'detecting' }
  | { status: 'on-pplkpr'; uid: string; displayName: string; tetherHandle: string | null }
  | { status: 'not-on-pplkpr' };

function ComposeView({
  contacts, uid, variant = 'clear', initialNewMode = false, initialPerson = null,
  initialText = '', onCancel, onSend, onChangeMatch,
}: {
  contacts: Contact[];
  uid: string | null;
  /** Which flow this composer serves — drives the explainer + eyebrow + f8 draft tone. */
  variant?: FlowVariant;
  /** PPL-127 — seed the picker in new-person mode (?compose=new deep link). */
  initialNewMode?: boolean;
  /**
   * Pre-picked target — a meet-a-match candidate (PPL-171) or a profile
   * "invite to room" (PPL-210). Skips the picker and the on-pplkpr lookup.
   */
  initialPerson?: ComposeTarget | null;
  /**
   * PPL-506 — the reason already typed before "change" bounced back to the
   * candidate list, handed back so a second look at the matches doesn't cost
   * the draft.
   */
  initialText?: string;
  onCancel: () => void;
  onSend: (
    target: ComposeTarget,
    reason: string,
    delivery: { mode: 'in_app' | 'email' | 'link'; resolvedUid?: string },
  ) => Promise<{ ok: boolean; error?: string }>;
  /**
   * PPL-506 — "change" on a match target belongs back in the candidate list,
   * not in the manual "who's it with" form. Only the parent knows that view
   * exists, so it hands the way back down.
   */
  onChangeMatch?: (draft: string) => void;
}) {
  const [person, setPerson] = useState<ComposeTarget | null>(initialPerson);
  const [picking, setPicking] = useState(!initialPerson);
  const [query, setQuery] = useState('');
  const [newMode, setNewMode] = useState(initialNewMode);
  const [newName, setNewName] = useState('');
  const [newContact, setNewContact] = useState('');
  const [text, setText] = useState(initialText);
  const [helping, setHelping] = useState(false);
  const [phrasingPending, setPhrasingPending] = useState(false);
  const [detection, setDetection] = useState<DetectionState>({ status: 'idle' });
  // Off-pplkpr delivery picker default: email if we have one, otherwise link.
  const [offChannel, setOffChannel] = useState<'email' | 'link'>('email');
  // PPL-126 — email found in the person's `contacts` subcollection (the person
  // doc itself rarely has one), and the typed-in fallback when nothing is on file.
  const [subEmail, setSubEmail] = useState('');
  const [typedEmail, setTypedEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const navigate = useNavigate();
  // PPL-506 — "invite to room" on the picked card is the SAME single invite
  // path: it moves the caller to the reason step this view already renders,
  // whose own primary button still does the sending.
  const textRef = useRef<HTMLTextAreaElement>(null);
  const goToComposeStep = () => {
    textRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    textRef.current?.focus();
  };

  const firstName = person ? person.name.split(' ')[0] : 'them';
  // Local fallback if the f8 opener call fails — same strings as the design
  // mock, so the button never leaves the user with nothing. Variant-toned:
  // clear-the-air assumes history; break-the-ice assumes none.
  const f8DraftFallback = variant === 'ice'
    ? `hi ${firstName} — we haven't met properly yet. i'd love a proper first conversation, and f8 can make it easy for both of us. want to give it a try?`
    : `i've been wanting to talk to you properly about something, and i think doing it with a bit of help might make it easier for both of us. would you sit with me in a room with f8?`;
  const detectionReady = detection.status === 'on-pplkpr' || detection.status === 'not-on-pplkpr';
  const knownEmail = (person?.email || subEmail).trim();
  const typedValid = /^\S+@\S+\.\S+$/.test(typedEmail.trim());
  const emailForDelivery = knownEmail || (typedValid ? typedEmail.trim() : '');
  // Emailing without an address can't be "ready" — the typed fallback must be valid.
  const deliveryReady =
    detection.status === 'on-pplkpr' || offChannel === 'link' || !!emailForDelivery;
  const ready = !!person && text.trim().length > 0 && !sending && detectionReady && deliveryReady;

  // PPL-60 — detect on-pplkpr after person pick. Handle wins; email lookup falls
  // back. PPL-126 — the person doc rarely carries an email, so pull one from the
  // contacts subcollection first; only truly email-less contacts fall to link.
  useEffect(() => {
    if (!person || picking) {
      setDetection({ status: 'idle' });
      setSubEmail('');
      setTypedEmail('');
      return;
    }
    // PPL-171 — a meet-a-match candidate arrives with its uid already known;
    // no lookup needed (and none possible — match targets aren't contacts).
    if (person.resolvedUid) {
      setDetection({ status: 'on-pplkpr', uid: person.resolvedUid, displayName: person.name, tetherHandle: person.tetherHandle || null });
      setOffChannel('email');
      return;
    }
    let cancelled = false;
    setDetection({ status: 'detecting' });
    (async () => {
      let email = (person.email || '').trim();
      try {
        // Handle first — it's the fast path and needs no email; the
        // subcollection read only matters for the email fallback (review).
        if (person.tetherHandle) {
          const r = await resolveTetherHandle(person.tetherHandle);
          if (cancelled) return;
          if (r.found) {
            setDetection({ status: 'on-pplkpr', uid: r.uid, displayName: r.displayName, tetherHandle: r.tetherHandle || null });
            setOffChannel('email');
            return;
          }
        }
        if (!email && !person.isNewPerson && uid) {
          try {
            const ch = await getContactChannels(uid, person.key);
            if (cancelled) return;
            if (ch.email) {
              email = ch.email;
              setSubEmail(ch.email);
            }
          } catch (err) {
            console.warn('rooms: contacts subcollection read failed', err);
          }
        }
        if (email) {
          const r = await lookupUserByEmail(email);
          if (cancelled) return;
          if (r.found) {
            setDetection({ status: 'on-pplkpr', uid: r.uid, displayName: r.displayName, tetherHandle: r.tetherHandle || null });
            setOffChannel('email');
            return;
          }
        }
        if (cancelled) return;
        setDetection({ status: 'not-on-pplkpr' });
        // Email stays the default even without one on file — the picker shows
        // a type-it-in fallback (PPL-126). Link remains one tap away.
        setOffChannel('email');
      } catch (err) {
        console.warn('rooms: detection failed', err);
        if (cancelled) return;
        setDetection({ status: 'not-on-pplkpr' });
        // Same default as the success path: the typed-email fallback means a
        // detection hiccup must not drop emailless contacts to link-only.
        setOffChannel('email');
      }
    })();
    return () => { cancelled = true; };
  }, [person, picking, uid]);

  // PPL-126 — a typed fallback email can still resolve to a pplkpr user; check
  // on blur so the flow flips to the richer on-pplkpr delivery when it does.
  const checkTypedEmail = async () => {
    const email = typedEmail.trim();
    if (!/^\S+@\S+\.\S+$/.test(email) || detection.status !== 'not-on-pplkpr') return;
    try {
      const r = await lookupUserByEmail(email);
      if (r.found) {
        setDetection({ status: 'on-pplkpr', uid: r.uid, displayName: r.displayName, tetherHandle: r.tetherHandle || null });
        setOffChannel('email');
      }
    } catch (err) {
      console.warn('rooms: typed-email lookup failed', err);
    }
  };

  const askF8 = async () => {
    if (!person || phrasingPending) return;
    setHelping(true);
    setPhrasingPending(true);
    try {
      const { opener } = await generateRoomOpener({
        inviteeName: person.name,
        relationship: person.rel || undefined,
        draft: text.trim() || undefined,
      });
      if (opener) setText(opener);
      else if (!text.trim()) setText(f8DraftFallback);
    } catch (err) {
      console.warn('rooms: generateRoomOpener failed, using fallback', err);
      if (!text.trim()) setText(f8DraftFallback);
    } finally {
      setPhrasingPending(false);
    }
  };

  return (
    <Stage variant={variant} breadcrumbLabel="your rooms" onBack={onCancel}>
      <div style={{ position: 'relative', paddingLeft: 18 }}>
        <div style={{
          position: 'absolute', left: 0, top: 4, bottom: 4,
          width: 3, borderRadius: 3, background: 'linear-gradient(#f0abfc,#d946ef)',
        }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, whiteSpace: 'nowrap' }}>
          <Dot size={13} />
          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#f0abfc' }}>
            {FLOW_EYEBROW[variant]}
          </span>
        </div>

        <div style={{
          padding: 22, borderRadius: 20,
          background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.09)',
        }}>
          <SectionLabel n="1" label="who's it with" />
          {person && !picking ? (
            <div style={{
              padding: '12px 14px', borderRadius: 14,
              background: `linear-gradient(135deg, hsla(${hueFromName(person.name)},75%,50%,0.13), transparent)`,
              border: `1px solid hsla(${hueFromName(person.name)},75%,55%,0.25)`,
              marginBottom: 22,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {person.avatar ? (
                  <img
                    src={person.avatar}
                    alt=""
                    style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                  />
                ) : (
                  <HueAvatar name={person.name} size={38} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Handle-only for anonymous-half targets — see ComposeTarget.name. */}
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{person.name}</div>
                  {person.rel && (
                    <div style={{ fontSize: 12, color: 'rgba(232,220,250,0.6)' }}>{person.rel}</div>
                  )}
                </div>
              </div>
              {/* PPL-506 / PPL-389 — a quiet row under the picked person: look
                  at them first, get on with the ask, or go back to where they
                  came from. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                {person.resolvedUid && (
                  <>
                    <button
                      onClick={() => {
                        stashComposeResume(person, text);
                        openProfile(navigate, person.resolvedUid!, person.source);
                      }}
                      style={pickedActionBtn}
                    >view profile</button>
                    <span style={{ color: 'rgba(232,220,250,0.35)', fontSize: 12.5 }}>·</span>
                  </>
                )}
                <button onClick={goToComposeStep} style={pickedActionBtn}>invite to room</button>
                <span style={{ color: 'rgba(232,220,250,0.35)', fontSize: 12.5 }}>·</span>
                <button
                  onClick={() => {
                    // PPL-506 — a match came from the candidate list, so
                    // "change" goes back to it. Anywhere else "change" still
                    // means the people picker. The typed reason rides along
                    // either way — the candidate list unmounts this view, so
                    // the draft goes up to the parent and comes back.
                    if (person.source === 'match' && onChangeMatch) onChangeMatch(text);
                    else setPicking(true);
                  }}
                  style={pickedActionBtn}
                >change</button>
              </div>
            </div>
          ) : (
            <PersonPicker
              contacts={contacts}
              query={query} setQuery={setQuery}
              newMode={newMode} setNewMode={setNewMode}
              newName={newName} setNewName={setNewName}
              newContact={newContact} setNewContact={setNewContact}
              onPick={(p) => { setPerson(p); setPicking(false); }}
            />
          )}

          {person && !picking && (
            <>
              <SectionLabel n="2" label="what do you want to say?" />
              <p style={{ fontSize: 13, color: 'rgba(232,220,250,0.6)', lineHeight: 1.55, margin: '0 0 12px', maxWidth: 560 }}>
                just the reason you&apos;re reaching out. no profile, no history — {firstName} sees exactly this.
              </p>
              <div style={{
                padding: '14px 16px', borderRadius: 14,
                background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)',
                marginBottom: 14,
              }}>
                <textarea
                  ref={textRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={4}
                  placeholder={`write to ${firstName}… or let f8 help you start`}
                  style={{
                    width: '100%', resize: 'none', background: 'transparent',
                    border: 0, outline: 'none', color: '#fff',
                    fontSize: 14.5, lineHeight: 1.55, fontFamily: 'inherit', padding: 0,
                  }}
                />
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginTop: 8, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)',
                }}>
                  <button
                    onClick={askF8}
                    disabled={phrasingPending}
                    style={{
                      appearance: 'none', cursor: phrasingPending ? 'progress' : 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 11px 6px 9px', borderRadius: 999,
                      background: helping ? 'rgba(217,70,239,0.18)' : 'rgba(255,255,255,0.04)',
                      border: helping ? '1px solid rgba(217,70,239,0.4)' : '1px solid rgba(255,255,255,0.08)',
                      color: helping ? '#f0abfc' : 'rgba(232,220,250,0.7)',
                      fontSize: 11.5, fontWeight: 600, fontFamily: 'inherit',
                    }}
                  >
                    <SparkleGlyph size={11} color={helping ? '#f0abfc' : 'rgba(232,220,250,0.7)'} />
                    <span style={{ whiteSpace: 'nowrap' }}>
                      {phrasingPending ? 'asking f8…' : 'ask f8 to help phrase this'}
                    </span>
                  </button>
                  <span style={{ fontSize: 11, color: 'rgba(232,220,250,0.4)' }}>{text.length} chars</span>
                </div>
              </div>
              {helping && (
                <div style={{
                  padding: '11px 14px', borderRadius: 12,
                  background: 'rgba(217,70,239,0.06)', border: '1px solid rgba(217,70,239,0.18)',
                  fontSize: 12.5, color: '#f3e8ff', lineHeight: 1.55, marginBottom: 18,
                }}>
                  here&apos;s a gentle opener — honest, no blame, and it doesn&apos;t ask {firstName} to fix anything yet. edit it to sound like you.
                </div>
              )}

              <div style={{
                padding: '12px 14px', borderRadius: 12,
                background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)',
                fontSize: 12.5, color: 'rgba(232,220,250,0.65)', lineHeight: 1.55, marginBottom: 22,
              }}>
                {firstName} will see exactly this and can{' '}
                <b style={{ color: '#fff', fontWeight: 600 }}>accept</b>,{' '}
                <b style={{ color: '#fff', fontWeight: 600 }}>decline</b>, or{' '}
                <b style={{ color: '#fff', fontWeight: 600 }}>ask to know more first</b>. nothing from your prep with f8 goes with this invitation.
              </div>

              {/* PPL-60 — delivery is auto-detected. on-pplkpr = pplkpr
                  notification + email, no picker. not-on-pplkpr = email or
                  copy-link choice. detecting = a short status row. */}
              <SectionLabel n="3" label="how this gets delivered" />

              {detection.status === 'detecting' && (
                <div style={{
                  padding: '12px 14px', borderRadius: 12, marginBottom: 22,
                  background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)',
                  fontSize: 13, color: 'rgba(232,220,250,0.65)',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <Loader size={14} className="rooms-spin" />
                  <span>checking if {firstName} is on pplkpr…</span>
                </div>
              )}

              {detection.status === 'on-pplkpr' && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '14px 16px', borderRadius: 14, marginBottom: 24,
                  background: 'linear-gradient(180deg, rgba(217,70,239,0.10), rgba(124,58,237,0.04))',
                  border: '1px solid rgba(217,70,239,0.24)',
                }}>
                  <img
                    src={iconImg}
                    alt="pplkpr"
                    width={28} height={28}
                    style={{ display: 'block', width: 28, height: 28, flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: '#fff' }}>
                      {firstName} is on pplkpr
                    </div>
                    <div style={{ fontSize: 12.5, color: 'rgba(232,220,250,0.65)', marginTop: 2 }}>
                      we&apos;ll send a notification and email this invitation.
                    </div>
                  </div>
                </div>
              )}

              {detection.status === 'not-on-pplkpr' && (
                <>
                  <div style={{
                    padding: '12px 14px', borderRadius: 12, marginBottom: 12,
                    background: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.22)',
                    fontSize: 12.5, color: '#fcd9b6', lineHeight: 1.55,
                  }}>
                    {firstName} isn&apos;t on pplkpr yet — choose how to reach them.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 24 }}>
                    {/* PPL-126 — email is always offered; without an address on
                        file it takes a typed one. */}
                    <ChannelRow
                      on={offChannel === 'email'}
                      onClick={() => setOffChannel('email')}
                      label="email them"
                      sub={knownEmail
                        ? `we'll send the invitation to ${knownEmail}`
                        : typedValid
                          ? `we'll send the invitation to ${typedEmail.trim()}`
                          : 'type their email address below'}
                      tag="recommended"
                      icon={<EmailGlyph />}
                    />
                    {!knownEmail && offChannel === 'email' && (
                      <input
                        type="email"
                        value={typedEmail}
                        onChange={(e) => setTypedEmail(e.target.value)}
                        onBlur={checkTypedEmail}
                        placeholder={`${firstName}'s email address`}
                        autoComplete="off"
                        style={{
                          margin: '2px 0 4px', padding: '11px 14px', borderRadius: 12,
                          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(217,70,239,0.28)',
                          outline: 'none', color: '#fff', fontSize: 13.5, fontFamily: 'inherit',
                        }}
                      />
                    )}
                    <ChannelRow
                      on={offChannel === 'link'}
                      onClick={() => setOffChannel('link')}
                      label="copy a link"
                      sub="we'll copy a private link to your clipboard — send it however you like"
                      icon={<LinkGlyph />}
                    />
                  </div>
                </>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <button
                  disabled={!ready}
                  onClick={async () => {
                    if (!person) return;
                    let delivery: { mode: 'in_app' | 'email' | 'link'; resolvedUid?: string };
                    if (detection.status === 'on-pplkpr') {
                      delivery = { mode: 'in_app', resolvedUid: detection.uid };
                    } else {
                      delivery = { mode: offChannel };
                    }
                    setSending(true);
                    setSendError(null);
                    // PPL-126 — carry the effective email (person doc, contacts
                    // subcollection, or typed fallback) into the send.
                    const target = emailForDelivery ? { ...person, email: emailForDelivery } : person;
                    const result = await onSend(target, text.trim(), delivery);
                    if (!result.ok) {
                      setSending(false);
                      setSendError(result.error || 'could not send the invitation. please try again.');
                    }
                    // On success, the parent unmounts this view — no need to reset state.
                  }}
                  style={{
                    appearance: 'none', border: 0, cursor: ready ? 'pointer' : 'not-allowed',
                    padding: '13px 24px', borderRadius: 999, fontFamily: 'inherit',
                    fontSize: 14, fontWeight: 700, color: '#fff',
                    background: ready ? 'linear-gradient(135deg,#d946ef,#a855f7)' : 'rgba(255,255,255,0.08)',
                    boxShadow: ready ? '0 8px 24px rgba(217,70,239,0.32)' : 'none',
                    opacity: ready ? 1 : 0.6,
                  }}
                >
                  {sending
                    ? 'sending…'
                    : detection.status === 'not-on-pplkpr' && offChannel === 'link'
                      ? 'create the link'
                      : 'send the invitation'}
                </button>
                <span style={{ fontSize: 12, color: 'rgba(232,220,250,0.5)', fontStyle: 'italic' }}>
                  they&apos;ll see it whenever they open it. nothing pushes at 11pm.
                </span>
              </div>
              {sendError && (
                <div style={{
                  marginTop: 12, padding: '10px 14px', borderRadius: 12,
                  background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                  fontSize: 12.5, color: '#fca5a5', lineHeight: 1.5,
                }}>{sendError}</div>
              )}
            </>
          )}
        </div>
      </div>
    </Stage>
  );
}

// ─── choose view ─────────────────────────────────────
//
// Entry chooser for composes started FROM the rooms page (list FAB / empty
// state) — the same two starters the chat home cards offer, so /rooms is a
// full entry point rather than defaulting to the clear-the-air composer.
// Deep-linked entries (?compose=) skip this — the card already chose.

function ChooseView({ onClear, onIce, onBack }: {
  onClear: () => void;
  onIce: () => void;
  onBack: () => void;
}) {
  const card = (glyph: React.ReactNode, title: string, sub: string, onClick: () => void) => (
    <button
      onClick={onClick}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(217,70,239,0.12)'; e.currentTarget.style.borderColor = 'rgba(217,70,239,0.5)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.035)'; e.currentTarget.style.borderColor = 'rgba(217,70,239,0.22)'; }}
      style={{
        appearance: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
        flex: '1 1 260px', display: 'flex', alignItems: 'center', gap: 16,
        padding: '20px 22px', borderRadius: 18,
        background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(217,70,239,0.22)',
        color: '#fff', transition: 'all 0.16s',
      }}
    >
      <span style={{
        width: 42, height: 42, borderRadius: 13, flexShrink: 0,
        background: 'rgba(217,70,239,0.12)', border: '1px solid rgba(217,70,239,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{glyph}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em' }}>{title}</div>
        <div style={{ fontSize: 13.5, color: 'rgba(232,220,250,0.6)', marginTop: 2 }}>{sub}</div>
      </div>
    </button>
  );

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '28px 0 60px' }}>
      <button
        onClick={onBack}
        style={{
          appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer',
          color: 'rgba(232,220,250,0.6)', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
          display: 'inline-flex', alignItems: 'center', gap: 6,
          marginBottom: 18, padding: 0, whiteSpace: 'nowrap',
        }}
      >
        <ChevronLeftGlyph size={14} /> your rooms
      </button>

      <div style={{ position: 'relative', paddingLeft: 18 }}>
        <div style={{
          position: 'absolute', left: 0, top: 4, bottom: 4,
          width: 3, borderRadius: 3, background: 'linear-gradient(#f0abfc,#d946ef)',
        }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, whiteSpace: 'nowrap' }}>
          <Dot size={13} />
          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#f0abfc' }}>
            f8 · opening a room
          </span>
        </div>

        <div style={{
          padding: 22, borderRadius: 20,
          background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.09)',
        }}>
          <SectionLabel n="1" label="what kind of conversation" />
          <p style={{ fontSize: 13, color: 'rgba(232,220,250,0.6)', lineHeight: 1.55, margin: '0 0 18px' }}>
            a room is a conversation with f8 in the middle — pick where this one starts.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {card(<Wind size={22} color="#f0abfc" />, 'clear the air', 'with someone you know', onClear)}
            {card(<Zap size={22} color="#f0abfc" />, 'break the ice', 'with someone new', onIce)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── fork view (PPL-132) ─────────────────────────────
//
// "Break the ice" entry fork, shown only when the meetAMatch feature flag is
// on AND the page was entered via ?compose=new. "Invite someone" continues
// into today's new-person composer branch; "Meet a match" is a dev-only
// inline placeholder until the PPL-130 candidate step lands (the flag stays
// off in production until then). Cancel/back returns to the chat home —
// unlike the composer's cancel, which returns to the rooms list.

function ForkView({ onInvite, onMatch, onCancel, backLabel = 'back to chat' }: {
  onInvite: () => void;
  onMatch: () => void;
  onCancel: () => void;
  backLabel?: string;
}) {
  // Same primitives as the composer's tappable affordances: radius 12,
  // 0.04 surface, 0.09 hairline, 28px icon badge.
  const optionCard = (on: boolean): CSSProperties => ({
    appearance: 'none', cursor: 'pointer', flex: '1 1 240px',
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8,
    padding: '16px 14px', borderRadius: 12, textAlign: 'left',
    background: on ? 'rgba(217,70,239,0.10)' : 'rgba(255,255,255,0.04)',
    border: on ? '1px solid rgba(217,70,239,0.4)' : '1px solid rgba(255,255,255,0.09)',
    color: '#fff', fontFamily: 'inherit',
  });

  return (
    <Stage variant="ice" breadcrumbLabel={backLabel} onBack={onCancel}>
      <div style={{ position: 'relative', paddingLeft: 18 }}>
        <div style={{
          position: 'absolute', left: 0, top: 4, bottom: 4,
          width: 3, borderRadius: 3, background: 'linear-gradient(#f0abfc,#d946ef)',
        }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, whiteSpace: 'nowrap' }}>
          <Dot size={13} />
          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#f0abfc' }}>
            {FLOW_EYEBROW.ice}
          </span>
        </div>

        <div style={{
          padding: 22, borderRadius: 20,
          background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.09)',
        }}>
          <SectionLabel n="1" label="who do you want to break the ice with" />
          <p style={{ fontSize: 13, color: 'rgba(232,220,250,0.6)', lineHeight: 1.55, margin: '0 0 18px', maxWidth: 540 }}>
            a careful first conversation, with f8 alongside you
          </p>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={onInvite} style={optionCard(false)}>
              <span style={{
                width: 28, height: 28, borderRadius: 9,
                background: 'rgba(217,70,239,0.16)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <UserPlus size={15} color="#f0abfc" />
              </span>
              <span style={{ fontSize: 13.5, fontWeight: 600 }}>invite someone</span>
              <span style={{ fontSize: 12.5, color: 'rgba(232,220,250,0.6)', lineHeight: 1.5 }}>
                someone you have in mind — invite them by name or email
              </span>
            </button>
            <button onClick={onMatch} style={optionCard(false)}>
              <span style={{
                width: 28, height: 28, borderRadius: 9,
                background: 'rgba(217,70,239,0.16)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Users size={15} color="#f0abfc" />
              </span>
              <span style={{ fontSize: 13.5, fontWeight: 600 }}>meet a match</span>
              <span style={{ fontSize: 12.5, color: 'rgba(232,220,250,0.6)', lineHeight: 1.5 }}>
                meet someone new on pplkpr — f8 makes the introduction
              </span>
            </button>
          </div>
        </div>
      </div>
    </Stage>
  );
}

// ─── match view (PPL-171) ────────────────────────────
//
// The real candidate step behind the fork's "meet a match" option, fed by
// getBreakTheIceMatchesEurW (PPL-170). Picking a candidate lands in the
// composer with the person pre-filled and detection skipped (resolvedUid).
// Back returns to the fork; the empty/error states offer the invite path so
// a dry pool never dead-ends the flow.

function MatchView({ onPick, onInvite, onBack }: {
  onPick: (c: MatchCandidate) => void;
  onInvite: () => void;
  onBack: () => void;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [candidates, setCandidates] = useState<MatchCandidate[]>([]);
  const [reloadKey, setReloadKey] = useState(0);
  // PPL-177 — interest-less profiles get weak (or, pre-fallback-deploy, zero)
  // matches, so the empty state nudges toward adding interests. null = unknown
  // (profile read pending/failed) → generic copy.
  const [hasInterests, setHasInterests] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    (async () => {
      try {
        const found = await getBreakTheIceMatches(4);
        if (cancelled) return;
        setCandidates(found);
        setStatus('ready');
        capture('match_shown', { count: found.length, platform: 'web' });
        // Only the empty state needs to know about interests — read lazily.
        if (found.length === 0 && user?.uid) {
          try {
            const profile = await getMyProfile(user.uid);
            if (cancelled) return;
            // interests is the canonical array on new writers and a CSV string
            // on older docs — the normaliser accepts both (PPL-490).
            setHasInterests(normalizeInterests(profile.interests).length > 0);
          } catch { /* unknown → generic empty copy */ }
        }
      } catch (err) {
        console.warn('rooms: getBreakTheIceMatches failed', err);
        if (cancelled) return;
        setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey, user?.uid]);

  // PPL-506 — the card is a wrapper now, not the button: "view profile" is a
  // real button and a button cannot nest inside one. The body button still
  // fills the card, so tapping the card still picks.
  const candidateCard: CSSProperties = {
    padding: '14px', borderRadius: 12,
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)',
  };

  const candidateBody: CSSProperties = {
    appearance: 'none', cursor: 'pointer', width: '100%', border: 0, padding: 0,
    display: 'flex', alignItems: 'flex-start', gap: 12, textAlign: 'left',
    background: 'transparent', color: '#fff', fontFamily: 'inherit',
  };

  const chip: CSSProperties = {
    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
    background: 'rgba(217,70,239,0.12)', border: '1px solid rgba(217,70,239,0.25)',
    color: '#f0abfc', whiteSpace: 'nowrap',
  };

  const activeLabel: Record<MatchCandidate['lastActiveBucket'], string> = {
    'today': 'active today',
    'this week': 'active this week',
    'this month': 'active recently',
  };

  return (
    <Stage variant="ice" breadcrumbLabel="back" onBack={onBack}>
      <div style={{ position: 'relative', paddingLeft: 18 }}>
        <div style={{
          position: 'absolute', left: 0, top: 4, bottom: 4,
          width: 3, borderRadius: 3, background: 'linear-gradient(#f0abfc,#d946ef)',
        }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, whiteSpace: 'nowrap' }}>
          <Dot size={13} />
          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#f0abfc' }}>
            f8 · meet a match
          </span>
        </div>

        <div style={{
          padding: 22, borderRadius: 20,
          background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.09)',
        }}>
          <SectionLabel n="1" label="people you might click with" />
          <p style={{ fontSize: 13, color: 'rgba(232,220,250,0.6)', lineHeight: 1.55, margin: '0 0 18px', maxWidth: 540 }}>
            picked for shared interests and being nearby — choose someone and f8 helps you break the ice
          </p>

          {status === 'loading' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 0', color: 'rgba(232,220,250,0.5)' }}>
              <Loader size={18} className="rooms-spin" />
              <span style={{ fontSize: 13 }}>finding your matches…</span>
            </div>
          )}

          {status === 'error' && (
            <div>
              <p style={{ fontSize: 13, color: 'rgba(232,220,250,0.7)', margin: '0 0 14px' }}>
                couldn&apos;t load matches right now.
              </p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button style={primaryBtn} onClick={() => setReloadKey((k) => k + 1)}>try again</button>
                <button style={ghostBtn} onClick={onInvite}>invite someone instead</button>
              </div>
            </div>
          )}

          {status === 'ready' && candidates.length === 0 && (
            <div>
              {hasInterests === false ? (
                <>
                  <p style={{ fontSize: 13, color: 'rgba(232,220,250,0.7)', margin: '0 0 14px', lineHeight: 1.55 }}>
                    your profile has no interests yet — add a few so f8 can find
                    your kind of people. matching leans on shared interests.
                  </p>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button style={primaryBtn} onClick={() => navigate('/profile/edit')}>add interests</button>
                    <button style={ghostBtn} onClick={onInvite}>invite someone instead</button>
                  </div>
                </>
              ) : (
                <>
                  <p style={{ fontSize: 13, color: 'rgba(232,220,250,0.7)', margin: '0 0 14px', lineHeight: 1.55 }}>
                    no matches right now — the pool grows as more people join. check back soon,
                    or invite someone you have in mind.
                  </p>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button style={primaryBtn} onClick={onInvite}>invite someone</button>
                    <button style={ghostBtn} onClick={onBack}>back</button>
                  </div>
                </>
              )}
            </div>
          )}

          {status === 'ready' && candidates.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {candidates.map((c, i) => (
                <div key={c.uid} style={candidateCard}>
                  <button
                    style={candidateBody}
                    onClick={() => {
                      capture('match_selected', {
                        candidate_uid: c.uid,
                        position: i,
                        shared_interest_count: c.sharedInterests.length,
                        platform: 'web',
                      });
                      onPick(c);
                    }}
                  >
                    {c.avatar ? (
                      <img
                        src={c.avatar}
                        alt=""
                        style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                      />
                    ) : (
                      <HueAvatar name={c.handle || '?'} size={44} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* ANONYMOUS register: discovery is @handle only, never
                          a real name (PPL-202). */}
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 14, fontWeight: 700 }}>
                          {c.handle ? `@${c.handle.replace(/^@/, '')}` : 'someone new'}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: 'rgba(232,220,250,0.55)', marginTop: 2 }}>
                        {[c.city, activeLabel[c.lastActiveBucket]].filter(Boolean).join(' · ')}
                      </div>
                      {c.aboutMe && (
                        <p style={{
                          fontSize: 12.5, color: 'rgba(232,220,250,0.7)', lineHeight: 1.5, margin: '6px 0 0',
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>
                          {c.aboutMe}
                        </p>
                      )}
                      {c.sharedInterests.length > 0 && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                          {c.sharedInterests.slice(0, 3).map((interest) => (
                            <span key={interest} style={chip}>{interest}</span>
                          ))}
                          {c.sharedInterests.length > 3 && (
                            <span style={{ ...chip, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(232,220,250,0.6)' }}>
                              +{c.sharedInterests.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <ChevronRightGlyph size={16} />
                  </button>
                  {/* PPL-389 — a look before the pick, without leaving the list
                      for the manual invite form. */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                    <button style={pickedActionBtn} onClick={() => openProfile(navigate, c.uid, 'match')}>
                      view profile
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Stage>
  );
}

// ─── list view rows ──────────────────────────────────

function RoomRow({ item, onOpen }: { item: RoomListItem; onOpen: () => void }) {
  const isMobile = useMediaQuery('(max-width: 560px)');
  const status = statusFor(item);
  const stage = stageFor(item);
  const them = item.otherParticipant.firstName || 'them';
  const fullName = `${item.otherParticipant.firstName || ''} ${item.otherParticipant.lastName || ''}`.trim() || them;
  const isInvite = status === 'invited' || status === 'waiting';
  const live = PILL_STYLES[status].live;
  return (
    // On mobile the fixed-width name/pill/time columns overflow a ~320px row,
    // so wrap: avatar + name + pill stay on line 1, the blurb + stage dots wrap
    // to line 2 (order: 1, full-width basis), and the secondary timestamp hides.
    <button onClick={onOpen} style={{
      appearance: 'none', cursor: 'pointer', width: '100%', textAlign: 'left',
      padding: isMobile ? '14px 16px' : '16px 20px', borderRadius: 16,
      background: 'rgba(255,255,255,0.03)',
      border: `1px solid ${isInvite ? 'rgba(251,146,60,0.3)' : 'rgba(255,255,255,0.08)'}`,
      display: 'flex', alignItems: 'center', flexWrap: isMobile ? 'wrap' : 'nowrap',
      gap: isMobile ? '10px 12px' : 16,
      color: '#fff', fontFamily: 'inherit',
    }}>
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <HueAvatar name={fullName} size={44} />
        <span style={{
          position: 'absolute', right: -2, bottom: -2,
          width: 16, height: 16, borderRadius: '50%',
          background: '#0e0712',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Dot size={10} pulse={live} />
        </span>
      </div>
      <div style={{ width: isMobile ? 'auto' : 150, flex: isMobile ? 1 : undefined, minWidth: 0, flexShrink: isMobile ? 1 : 0 }}>
        <div style={{ fontSize: 15.5, fontWeight: 700, letterSpacing: '-0.01em' }}>{them}</div>
        {/* @handle intentionally not shown in the rooms list (PPL-162). */}
        {item.state === 'invited' && item.isInviter && item.inviteeEmail && (
          <div style={{
            fontSize: 11.5, color: 'rgba(232,220,250,0.45)', marginTop: 2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {item.inviteeEmail}
          </div>
        )}
      </div>
      <div style={{
        flex: 1, minWidth: 0, fontSize: 13, color: 'rgba(232,220,250,0.72)', lineHeight: 1.45,
        order: isMobile ? 1 : 0, flexBasis: isMobile ? '100%' : 'auto',
      }}>
        {blurbFor(item)}
        <div style={{ marginTop: 8 }}><StageDots stage={stage} /></div>
      </div>
      <StatusPill status={status} />
      {!isMobile && (
        <span style={{ fontSize: 11.5, color: 'rgba(232,220,250,0.4)', fontWeight: 600, width: 44, textAlign: 'right' }}>
          {timeAgo(item.lastActivityAt || item.createdAt)}
        </span>
      )}
    </button>
  );
}

function EndedRow({ item, onOpen }: { item: RoomListItem; onOpen: () => void }) {
  const them = item.otherParticipant.firstName || 'them';
  const fullName = `${item.otherParticipant.firstName || ''} ${item.otherParticipant.lastName || ''}`.trim() || them;
  return (
    <button onClick={onOpen} style={{
      appearance: 'none', cursor: 'pointer', width: '100%', textAlign: 'left',
      padding: '10px 16px', borderRadius: 12,
      background: 'transparent', border: '1px solid rgba(255,255,255,0.05)',
      display: 'flex', alignItems: 'center', gap: 12,
      color: 'rgba(232,220,250,0.6)', fontFamily: 'inherit',
    }}>
      <HueAvatar name={fullName} size={28} />
      <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(232,220,250,0.75)', flexShrink: 0 }}>{them}</span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{blurbFor(item)}</span>
      <span style={{ fontSize: 11.5, color: 'rgba(232,220,250,0.35)', flexShrink: 0 }}>{timeAgo(item.lastActivityAt || item.createdAt)}</span>
    </button>
  );
}

function ListView({
  active, ended, showEnded, setShowEnded, onCompose, onOpenRoom,
}: {
  active: RoomListItem[];
  ended: RoomListItem[];
  showEnded: boolean;
  setShowEnded: (b: boolean) => void;
  onCompose: () => void;
  onOpenRoom: (item: RoomListItem) => void;
}) {
  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 0 80px' }}>
      <div style={{
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        gap: 16, marginBottom: 26, flexWrap: 'wrap',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, letterSpacing: '-0.03em' }}>your rooms</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: 'rgba(232,220,250,0.55)' }}>conversations with f8 as a third presence</p>
        </div>
        <button onClick={onCompose} style={primaryBtn}>
          <RoomsGlyph size={17} color="#fff" /> open a room
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {active.map((item) => (
          <RoomRow key={item.id} item={item} onOpen={() => onOpenRoom(item)} />
        ))}
      </div>

      {ended.length > 0 && (
        <div style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <button
            onClick={() => setShowEnded(!showEnded)}
            aria-expanded={showEnded}
            style={{
              appearance: 'none', cursor: 'pointer', background: 'transparent', border: 0, padding: '6px 0',
              color: 'rgba(232,220,250,0.5)', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: 6, textTransform: 'lowercase',
            }}
          >
            {showEnded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            <span>{ended.length} ended</span>
          </button>
          {showEnded && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
              {ended.map((item) => (
                <EndedRow key={item.id} item={item} onOpen={() => onOpenRoom(item)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── toast ───────────────────────────────────────────

function Toast({ message }: { message: string }) {
  return (
    <div style={{
      position: 'fixed', left: '50%', bottom: 28, transform: 'translateX(-50%)', zIndex: 50,
      display: 'flex', alignItems: 'center', gap: 11,
      padding: '13px 20px', borderRadius: 14,
      background: 'rgba(28,16,38,0.96)', border: '1px solid rgba(34,197,94,0.4)',
      boxShadow: '0 16px 40px rgba(0,0,0,0.5)',
      animation: 'rooms-rise 0.4s ease',
    }}>
      <Check size={18} color="#86efac" />
      <span style={{ fontSize: 13.5, fontWeight: 600, color: '#fff' }}>{message}</span>
    </div>
  );
}

// ─── main ────────────────────────────────────────────

type View = 'empty' | 'choose' | 'fork' | 'match' | 'compose' | 'list';

export default function RoomList() {
  const { user } = useAuth();
  const { isAuthenticated } = useAuthGate();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [pending, setPending] = useState<RoomListItem[]>([]);
  const [mediations, setRooms] = useState<RoomListItem[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEnded, setShowEnded] = useState(false);
  const [view, setView] = useState<View>('empty');
  const [composeNewMode, setComposeNewMode] = useState(false);
  // Which flow the compose stage serves: 'ice' when entered via the
  // break-the-ice card (?compose=new → fork/match/new-person) or the /rooms
  // chooser's ice option, 'clear' otherwise.
  const [flowVariant, setFlowVariant] = useState<FlowVariant>('clear');
  // Where the fork's back button returns to: the chat home for deep-linked
  // entries, the /rooms chooser when entered from this page.
  const [forkReturn, setForkReturn] = useState<'chat' | 'choose'>('chat');
  // The person the composer opens with already chosen — a picked meet-a-match
  // candidate (PPL-171) or a profile "invite to room" target (PPL-210). Either
  // way the composer skips the picker and lands on the reason/send step.
  const [prefillTarget, setPrefillTarget] = useState<ComposeTarget | null>(null);
  // PPL-506 — the reason typed before "change" sent the caller back to the
  // candidate list. Handed to the next composer and spent there (the effect
  // below), so an unrelated compose never opens on someone else's draft.
  const matchDraft = useRef('');
  // PPL-506 — every way out of the match pick that is not "pick someone else"
  // abandons the draft: it was written for a candidate the user walked away
  // from, so a manual name + email invite must not open pre-filled with it.
  const abandonMatchDraft = () => {
    matchDraft.current = '';
    clearComposeResume();
  };
  const [toast, setToast] = useState<string | null>(null);

  // PPL-127 — the chat-home CTA cards deep-link here as ?compose=existing|new.
  // Capture the intent into a ref BEFORE stripping the param: the initial view
  // is decided inside the async load below, after the URL is already clean, so
  // the captured value (not the URL) must drive that decision.
  const composeIntent = useRef<'existing' | 'new' | null>(null);
  useEffect(() => {
    const compose = searchParams.get('compose');
    if (compose === 'existing' || compose === 'new') {
      composeIntent.current = compose;
      const next = new URLSearchParams(searchParams);
      next.delete('compose');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // PPL-210 — "invite to room" on /user/:userId hands us the person already
  // resolved, in router state rather than the URL (a uid in the address bar is
  // a shareable identifier we don't want). Same one-shot shape as ?compose=
  // above: capture into a ref, then clear the state, because the initial view
  // is decided inside the async load below — after this has run.
  const profileTarget = useRef<ComposeTarget | null>(null);
  useEffect(() => {
    const incoming = (location.state as { roomTarget?: RoomComposeTarget } | null)?.roomTarget;
    if (!incoming?.uid) return;
    const handle = (incoming.handle || '').replace(/^@/, '');
    profileTarget.current = {
      key: `profile:${incoming.uid}`,
      // ANONYMOUS half (discovery / public profile): the @handle is the only
      // name that exists for this person here — never a firstName (PPL-202).
      name: handle ? `@${handle}` : NAMELESS_TARGET,
      isNameless: !handle,
      // No relationship is known from a profile, and the composer has no
      // picker for one — send nothing rather than inventing 'new-connection'.
      rel: '',
      isNewPerson: false,
      tetherHandle: handle || undefined,
      avatar: incoming.avatar || undefined,
      resolvedUid: incoming.uid,
      // PPL-417 — the home desk's meet-someone module hands over the same
      // shape, attributed 'match' so room_created cuts the way the fork's
      // match step does.
      source: incoming.source === 'match' ? 'match' : 'profile',
    };
    // Consume it: a back/forward or refresh must not re-open the composer.
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate]);

  // PPL-417 — the home desk's "clear the air" offer names one of MY people
  // by id (personal register: my own contact, never a uid) alongside
  // ?compose=existing, so the composer opens on them instead of the picker.
  // Same one-shot shape as the two above; resolved against the contact list
  // inside the load below, since that is where the contacts arrive.
  const personTarget = useRef<string | null>(null);
  useEffect(() => {
    const incoming = (location.state as { personId?: unknown } | null)?.personId;
    if (typeof incoming !== 'string' || !incoming) return;
    personTarget.current = incoming;
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // PPL-132 — only the ?compose=new deep link needs the flag, so only
        // that intent starts a read at all (plain visits and 'existing' never
        // touch featureFlags). Started before the room load so the two
        // overlap, and never gating it: the helper never rejects (errors →
        // false) and the bounded timeout means a slow flag read can never
        // hold the compose flow hostage — it resolves off.
        const meetAMatchP = composeIntent.current === 'new'
          ? withTimeout(isMeetAMatchEnabled(), FLAG_READ_TIMEOUT_MS, false)
          : null;
        const [med, contactRes] = await Promise.all([
          getMyRooms(),
          user?.uid ? getContacts(user.uid, 200) : Promise.resolve({ contacts: [], lastDoc: null }),
        ]);
        if (cancelled) return;
        setPending(med.pendingInvitations || []);
        setRooms(med.mediations || []);
        setContacts(contactRes.contacts || []);
        // Initial view: a captured profile target wins (PPL-210), then a
        // ?compose= intent (PPL-127), otherwise list if there are rooms, empty
        // if not. Composer is entered explicitly.
        const fromProfile = profileTarget.current;
        const intent = composeIntent.current;
        // A person the desk named who is not in my list (deleted since, or
        // beyond the 200 loaded) falls through to the ?compose=existing
        // picker; either way the one-shot is spent.
        const fromPerson = personTarget.current ? (contactRes.contacts || []).find((c) => c.id === personTarget.current) : undefined;
        personTarget.current = null;
        if (fromProfile) {
          // One-shot, same as the ?compose= intent: a later manual compose
          // must start from a clean picker.
          profileTarget.current = null;
          // PPL-506 — came back through the profile's own "invite to room" for
          // the person we left with: hand the parked reason back.
          const parked = takeComposeResume();
          matchDraft.current = parked && parked.target.resolvedUid === fromProfile.resolvedUid
            ? parked.draft
            : '';
          setPrefillTarget(fromProfile);
          setComposeNewMode(false);
          // Discovery/profile targets are people you haven't spoken to here —
          // the break-the-ice explainer is the honest framing.
          setFlowVariant('ice');
          setView('compose');
        } else if (fromPerson) {
          // Consumes the ?compose=existing that rode along with the personId.
          composeIntent.current = null;
          setPrefillTarget(contactTarget(fromPerson));
          setComposeNewMode(false);
          setFlowVariant('clear');
          setView('compose');
        } else if (intent) {
          // One-shot: consume the intent so later manual compose entries
          // (ComposeView remounts) don't re-open new-person mode.
          composeIntent.current = null;
          setFlowVariant(intent === 'new' ? 'ice' : 'clear');
          // Flag latency only ever affects the break-the-ice deep link.
          const meetAMatch = meetAMatchP ? await meetAMatchP : false;
          if (cancelled) return;
          if (intent === 'new' && meetAMatch) {
            // PPL-132 — flag on: "break the ice" lands on the entry fork
            // first. Flag off (or 'existing' intent): straight to the
            // composer, byte-identical to today.
            setForkReturn('chat');
            setView('fork');
          } else {
            setComposeNewMode(intent === 'new');
            setView('compose');
          }
        } else {
          // PPL-506 — browser back from a candidate's profile lands here with
          // no router state at all; the parked pick and reason are what stop
          // that from costing the whole flow.
          const parked = takeComposeResume();
          if (parked) {
            setPrefillTarget(parked.target);
            matchDraft.current = parked.draft;
            setComposeNewMode(false);
            setFlowVariant('ice');
            setView('compose');
          } else {
            const totalRooms = (med.mediations?.length || 0) + (med.pendingInvitations?.length || 0);
            setView(totalRooms > 0 ? 'list' : 'empty');
          }
        }
      } catch (err: unknown) {
        if (cancelled) return;
        // The load is where both one-shots are consumed, so a failed load has
        // to consume them too — otherwise a later re-run (auth resolving, a
        // uid change) pops the composer open on a target the user has long
        // since moved on from. Cleared inside the `!cancelled` branch on
        // purpose: StrictMode's discarded first run must not eat the ref the
        // surviving run still needs.
        profileTarget.current = null;
        personTarget.current = null;
        composeIntent.current = null;
        console.error('getMyRooms/getContacts failed', err);
        setError(err instanceof Error ? err.message : 'could not load your rooms');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const allRooms = useMemo(() => {
    const combined = [...pending, ...mediations];
    const seen = new Set<string>();
    return combined.filter((r) => {
      if (seen.has(r.id)) return false;
      seen.add(r.id);
      return true;
    });
  }, [pending, mediations]);

  const active = useMemo(() => allRooms.filter((m) => m.state !== 'ended'), [allRooms]);
  const ended  = useMemo(() => allRooms.filter((m) => m.state === 'ended'), [allRooms]);

  // Composer send handler.
  //
  // Direct path (PPL-54): when the picked contact is linked to a pplkpr user
  // (i.e. has a tetherHandle), resolve handle → uid and call
  // createRoomInvite directly. On success, refetch the rooms list and
  // switch to it.
  //
  // Handoff path: when the contact isn't linked (or is brand-new), bounce to
  // /f8chat with the composed context so f8 can do the lookup / non-app flow.
  // This keeps email/sms/link channels for unlinked invitees working without
  // duplicating that machinery on this page.
  // PPL-60 — three delivery modes, all hitting the same createRoomInvite
  // callable. mode is determined by the composer based on on-pplkpr detection:
  //   in_app  → on-pplkpr; server sends notification + email automatically.
  //   email   → not-on-pplkpr + email channel; server creates placeholder + emails.
  //   link    → not-on-pplkpr + copy-link; server returns inviteUrl, we copy it.
  const handleSend = async (
    target: ComposeTarget,
    reason: string,
    delivery: { mode: 'in_app' | 'email' | 'link'; resolvedUid?: string },
  ): Promise<{ ok: boolean; error?: string }> => {
    const first = target.name.split(' ')[0];
    try {
      const args: { inviteeId?: string; inviteeEmail?: string; inviteeName?: string; reason: string; channel: 'in_app' | 'email' | 'link'; inviterRelationship?: string; source: string } = {
        reason,
        channel: delivery.mode,
        // 'match' = meet-a-match candidate step (PPL-171), 'profile' = the
        // "invite to room" action on a user profile (PPL-210); anything picked
        // by hand here is the rooms-page composer.
        source: target.source || 'composer',
      };
      // The typed name travels with the invite so the pending row on /rooms
      // (and the placeholder invite email) can greet by name instead of "them".
      // The nameless stand-in is skipped: the server would persist it as the
      // invitee's display name and greet a literal "them" forever after.
      const typedName = target.name.trim();
      if (typedName && !target.isNameless) {
        args.inviteeName = typedName;
      }
      // PPL-129 — free-text rel → enum key ('new connection' → 'new-connection',
      // placeholder/empty → undefined and the field is omitted).
      const inviterRelationship = toInviterRelationship(target.rel);
      if (inviterRelationship) {
        args.inviterRelationship = inviterRelationship;
      }
      if (delivery.resolvedUid) {
        args.inviteeId = delivery.resolvedUid;
      } else if (target.email) {
        args.inviteeEmail = target.email;
      }
      const result = await createRoomInvite(args);
      if (result && result.success === false) {
        return { ok: false, error: result.reason || 'could not send the invitation.' };
      }

      // An ACTIVE room with this person already exists: the server returns the
      // existing mediation untouched (created:false) and sends NOTHING — no
      // notification, no email. Surface the truth and take the user to the
      // existing room instead of toasting a send that never happened.
      if (result && result.created === false) {
        const fresh = await getMyRooms();
        setPending(fresh.pendingInvitations || []);
        setRooms(fresh.mediations || []);
        setPrefillTarget(null);
        const existing = [...(fresh.pendingInvitations || []), ...(fresh.mediations || [])]
          .find((m) => m.id === result.mediationId);
        // Pending invitation + caller is the inviter → the server re-delivered
        // the invite email (hourly-throttled). Say which of the three happened.
        setToast(result.resent
          ? `invitation sent again to ${first}`
          : result.resendThrottled
            ? `already re-sent recently — give ${first} a little time`
            : existing
              ? `you already have an open room with ${first} — taking you there`
              : `you already have an open room with ${first}`);
        setTimeout(() => setToast(null), 4200);
        if (existing) {
          navigate(routeFor(existing));
        } else {
          setView('list');
        }
        return { ok: true };
      }

      if (delivery.mode === 'link' && result?.inviteUrl) {
        try {
          await navigator.clipboard.writeText(result.inviteUrl);
          setToast(`link copied for ${first}`);
        } catch {
          // Clipboard write blocked (e.g. iOS without a user gesture). Show
          // the URL so the user can copy it manually.
          window.prompt('copy this link to send:', result.inviteUrl);
          setToast(`link ready for ${first}`);
        }
      } else {
        setToast(`invitation on its way to ${first}`);
      }
      setTimeout(() => setToast(null), 4200);

      // Refresh the rooms list so any new "invite sent" row appears.
      const fresh = await getMyRooms();
      setPending(fresh.pendingInvitations || []);
      setRooms(fresh.mediations || []);
      setPrefillTarget(null);
      setView('list');
      return { ok: true };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'unknown error';
      console.error('rooms: createRoomInvite failed', msg);
      return { ok: false, error: msg };
    }
  };

  // Flow views (fork/match/compose) run the two-column stage on desktop and
  // need more width than the list; the list keeps its tighter measure.
  // One-shot: the composer reads the carried draft on mount, so clearing it
  // right after that render leaves every later entry with a clean textarea.
  useEffect(() => {
    if (view === 'compose') matchDraft.current = '';
  }, [view]);

  const isFlowView = view === 'fork' || view === 'match' || view === 'compose';
  return (
    <div style={{ maxWidth: isFlowView ? 1120 : 880, margin: '0 auto', padding: '32px 24px 80px', color: '#f5f3ff' }}>
      <SEO title="your rooms — pplkpr" description="conversations with f8 as a third presence" />

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '40px 0', color: 'rgba(232,220,250,0.5)' }}>
          <Loader size={20} className="rooms-spin" />
          <span>loading…</span>
        </div>
      )}

      {error && !loading && (
        <div style={{
          padding: 20, borderRadius: 16,
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <AlertCircle size={18} color="#fca5a5" />
          <div>
            <div style={{ fontWeight: 700, color: '#fca5a5' }}>couldn&apos;t load your rooms</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{error}</div>
          </div>
        </div>
      )}

      {!loading && !error && view === 'empty' && (
        <EmptyView
          onOpen={() => { setPrefillTarget(null); setView('choose'); }}
          onTalk={() => navigate('/f8chat')}
        />
      )}

      {!loading && !error && view === 'choose' && (
        <ChooseView
          onClear={() => { abandonMatchDraft(); setPrefillTarget(null); setComposeNewMode(false); setFlowVariant('clear'); setView('compose'); }}
          onIce={async () => {
            abandonMatchDraft();
            setPrefillTarget(null);
            setFlowVariant('ice');
            // Module-cached after the first read, so this is instant on
            // repeat visits; bounded either way.
            const meetAMatch = await withTimeout(isMeetAMatchEnabled(), FLAG_READ_TIMEOUT_MS, false);
            if (meetAMatch) {
              setForkReturn('choose');
              setView('fork');
            } else {
              setComposeNewMode(true);
              setView('compose');
            }
          }}
          onBack={() => setView(allRooms.length > 0 ? 'list' : 'empty')}
        />
      )}

      {!loading && !error && view === 'fork' && (
        <ForkView
          onInvite={() => { abandonMatchDraft(); setPrefillTarget(null); setComposeNewMode(true); setView('compose'); }}
          onMatch={() => setView('match')}
          backLabel={forkReturn === 'choose' ? 'back' : 'back to chat'}
          onCancel={() => {
            abandonMatchDraft();
            return forkReturn === 'choose' ? setView('choose') : navigate('/f8chat');
          }}
        />
      )}

      {!loading && !error && view === 'match' && (
        <MatchView
          onPick={(c) => {
            setPrefillTarget({
              key: `match:${c.uid}`,
              name: c.handle ? `@${c.handle.replace(/^@/, '')}` : 'someone new',
              rel: 'new connection',
              isNewPerson: false,
              tetherHandle: c.handle ? c.handle.replace(/^@/, '') : undefined,
              resolvedUid: c.uid,
              source: 'match',
            });
            setComposeNewMode(false);
            setView('compose');
          }}
          onInvite={() => { abandonMatchDraft(); setPrefillTarget(null); setComposeNewMode(true); setView('compose'); }}
          onBack={() => { abandonMatchDraft(); setView('fork'); }}
        />
      )}

      {!loading && !error && view === 'compose' && (
        <ComposeView
          contacts={contacts}
          uid={user?.uid || null}
          variant={flowVariant}
          initialNewMode={composeNewMode}
          initialPerson={prefillTarget}
          initialText={matchDraft.current}
          onCancel={() => { abandonMatchDraft(); setPrefillTarget(null); setView(allRooms.length > 0 ? 'list' : 'empty'); }}
          onSend={handleSend}
          onChangeMatch={(draft) => {
            // PPL-506 — back to the people you might click with, not to the
            // manual name + email form.
            matchDraft.current = draft;
            clearComposeResume();
            setPrefillTarget(null);
            setView('match');
          }}
        />
      )}

      {!loading && !error && view === 'list' && (
        <ListView
          active={active}
          ended={ended}
          showEnded={showEnded}
          setShowEnded={setShowEnded}
          onCompose={() => {
            capture('room_cta_tapped', {
              cta: 'open_a_room',
              action: 'create',
              mediation_id: null,
              platform: 'web',
            });
            abandonMatchDraft();
            setPrefillTarget(null);
            setView('choose');
          }}
          onOpenRoom={(item) => {
            // Opening an ended room is neither create nor resume — only active
            // rows are a resume entry point, so ended rows emit nothing.
            if (item.state !== 'ended') {
              capture('room_cta_tapped', {
                cta: 'room_list_row',
                action: 'resume',
                mediation_id: item.id,
                platform: 'web',
              });
            }
            navigate(routeFor(item));
          }}
        />
      )}

      {toast && <Toast message={toast} />}
    </div>
  );
}
